/**
 * The JSON contract between the fetcher (producer) and the web app (consumer).
 *
 * Both packages import these types so the two sides cannot drift. When adding a
 * new award, add its id to `AwardId`, a matching entry to `AWARD_DEFINITIONS`,
 * and a `<AwardId>Award` shape below, then key it into `SeasonAwards`.
 */

/** Identifier for a computed award. One string per award we track. */
export type AwardId = "biggestBlowout" | "seasonStandings";

/** Whether a given week's matchups have finished. */
export type WeekStatus = "final" | "in_progress" | "not_started";

/** A team as it appears inside a matchup result, with its score for that week. */
export interface TeamScore {
  teamKey: string;
  name: string;
  points: number;
}

/** Full team metadata for a season (used for logos, manager names, etc.). */
export interface Team {
  teamKey: string;
  teamId: number;
  name: string;
  managerNames: string[];
  logoUrl: string | null;
}

/** One week's "biggest blowout" result. */
export interface BlowoutWeek {
  week: number;
  status: WeekStatus;
  /** Present when the week has a decided matchup (final or in progress). */
  winner: TeamScore | null;
  loser: TeamScore | null;
  /** winner.points - loser.points, rounded to 2dp. Null when undecided. */
  margin: number | null;
}

/** A row in an award's running season tally. */
export interface AwardStanding {
  teamKey: string;
  name: string;
  count: number;
  weeksWon: number[];
}

/** The "biggest blowout" award for a whole season. */
export interface BlowoutAward {
  weeks: BlowoutWeek[];
  standings: AwardStanding[];
}

/** One team's regular-season win-loss record and points (see `SeasonStandingsAward`). */
export interface TeamRecord {
  teamKey: string;
  name: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
}

/** Regular-season standings for a whole season, ranked best-record first. */
export interface SeasonStandingsAward {
  standings: TeamRecord[];
}

/** All awards computed for a season, keyed by award id. */
export interface SeasonAwards {
  biggestBlowout?: BlowoutAward;
  seasonStandings?: SeasonStandingsAward;
}

/** The `data/seasons/<year>.json` file. */
export interface SeasonArchive {
  season: number;
  leagueKey: string;
  leagueName: string;
  /** True once every week 1-14 is final; the fetcher then stops refetching it. */
  complete: boolean;
  generatedAt: string;
  teams: Team[];
  awards: SeasonAwards;
}

/** The `data/manifest.json` file — the entry point the web app loads first. */
export interface Manifest {
  generatedAt: string;
  leagueName: string;
  currentSeason: number;
  /** Ascending list of seasons that have a `data/seasons/<year>.json` file. */
  seasons: number[];
}

/**
 * How a `PayoutAward` category's winner is resolved each season. Omitted
 * (or `"manual"`): an admin assigns the winning team via
 * `SeasonPayoutWinners`. `"blowoutWeekly"`/`"seasonStandings"`: resolved
 * automatically from that season's computed `SeasonAwards` instead — see
 * `PrizePayoutsView`.
 */
export const PAYOUT_AWARD_SOURCES = ["manual", "blowoutWeekly", "seasonStandings"] as const;
export type PayoutAwardSource = (typeof PAYOUT_AWARD_SOURCES)[number];

/** One line item in the league's prize payout structure (see `PayoutStructure`). */
export interface PayoutAward {
  label: string;
  amount: number;
  description?: string;
  source?: PayoutAwardSource;
}

/**
 * The `data/payouts.json` file — the league's current prize payout
 * structure: a single site-wide document, not tied to any season, edited
 * directly by a logged-in admin (see `packages/api/src/payoutsEditor.ts`).
 * Deliberately not part of the `AwardId`/`AWARD_DEFINITIONS` registry above
 * — nothing here is computed from `WeekInput[]` the way `SeasonAwards` is;
 * the admin's input *is* the published document. Which team won which
 * category each season lives separately, in `SeasonPayoutWinners` below,
 * so this structure (categories/amounts) doesn't need re-entering every year.
 */
export interface PayoutStructure {
  updatedAt: string;
  buyInPerTeam: number;
  totalPool: number;
  awards: PayoutAward[];
  /** e.g. how any pool balance left over after awards is spent. */
  note?: string;
}

/** One manually-assigned award winner for a season (see `SeasonPayoutWinners`). */
export interface PayoutWinner {
  /** Matches a `PayoutStructure.awards[].label`. */
  awardLabel: string;
  teamName: string;
}

/**
 * The `data/payout-winners/<season>.json` file — which team won each
 * manually-awarded prize category that season. Only categories whose
 * `PayoutAward.source` is `"manual"` (or unset) get an entry here; the
 * `"blowoutWeekly"`/`"seasonStandings"` categories are resolved directly
 * from that season's `SeasonArchive.awards` instead, so there's nothing to
 * duplicate or fall out of sync.
 *
 * Keyed by the award's `label` and the winning team's *name* — same
 * name-based join rationale as `SeasonDues`/`SeasonManagerAliases` (see
 * their doc comments): a manually entered season's `teamKey` isn't a
 * stable join key.
 */
export interface SeasonPayoutWinners {
  season: number;
  updatedAt: string;
  winners: PayoutWinner[];
}

/** One team's buy-in payment status for a season (see `SeasonDues`). */
export interface TeamDues {
  name: string;
  status: "due" | "paid";
}

/**
 * The `data/dues/<season>.json` file — that season's buy-in payment status
 * per team (the amount itself comes from `PayoutStructure.buyInPerTeam`).
 * Directly admin-set, not computed, so — like `PayoutStructure` — it's not
 * part of the `AwardId`/`AWARD_DEFINITIONS` registry above. Reuses that
 * season's own roster (`SeasonArchive.teams`) rather than a separate team
 * list, so there's nowhere new to keep team names in sync.
 *
 * Keyed by team *name*, not `teamKey`: a manually-entered season's
 * `teamKey` is re-minted from roster order on every edit (see
 * `seasonEditor.ts`'s `mintTeams`), so it isn't a safe join key across
 * writes — this mirrors `ManualWeekResult`, which keys results by name for
 * the same reason. A team in the season's roster with no entry here (a
 * new addition, or nothing set for this season yet) is treated as "due".
 */
export interface SeasonDues {
  season: number;
  updatedAt: string;
  teams: TeamDues[];
}

/**
 * One team's manager display-name override for a season (see
 * `SeasonManagerAliases`). `managerName` is the real name to show instead
 * of Yahoo's nickname; `""` means "no override, fall back to Yahoo's
 * current `Team.managerNames`".
 */
export interface TeamManagerAlias {
  name: string;
  managerName: string;
}

/**
 * The `data/manager-aliases/<season>.json` file — that season's manager
 * real-name overrides. Yahoo only exposes a manager's self-chosen, mutable
 * nickname (no stable id survives into this pipeline — see
 * `parseTeamMeta`), and league members change it often, so this lives
 * entirely outside the Yahoo-derived `SeasonArchive`: the weekly fetch
 * never reads or writes it, which is what lets an override survive the
 * next Tuesday's rebuild.
 *
 * Keyed by team *name*, scoped per season — same rationale as `SeasonDues`
 * above (see its doc comment): no Yahoo-provided id is stable enough to
 * join on, and team identity itself resets every season anyway (Yahoo
 * mints a new league/team key each year, and the same roster slot can
 * change hands between people). A team with no entry here just shows
 * Yahoo's own nickname(s), unchanged.
 */
export interface SeasonManagerAliases {
  season: number;
  updatedAt: string;
  teams: TeamManagerAlias[];
}

/** Static description of an award, shared by producer and consumer for headings. */
export interface AwardDefinition {
  id: AwardId;
  label: string;
  description: string;
  firstWeek: number;
  lastWeek: number;
}

export const AWARD_DEFINITIONS: readonly AwardDefinition[] = [
  {
    id: "biggestBlowout",
    label: "Biggest Blowout",
    // Intentionally blank: the page shows no subtitle for this award.
    description: "",
    firstWeek: 1,
    lastWeek: 14,
  },
  {
    id: "seasonStandings",
    label: "Season Standings",
    description: "Regular-season win-loss record.",
    firstWeek: 1,
    lastWeek: 14,
  },
];

export function getAwardDefinition(id: AwardId): AwardDefinition {
  const def = AWARD_DEFINITIONS.find((d) => d.id === id);
  if (!def) throw new Error(`Unknown award id: ${id}`);
  return def;
}

/**
 * The hand-curated contract for data entered by a logged-in user rather than
 * sourced from Yahoo — mirrors only what the weekly table shows (winner +
 * score, loser + score). Margin and standings are always derived by
 * `computeBlowoutAward`, never typed by hand. Stored as the raw input behind
 * `data/manual/<season>.json` (see `DataStore.readManualInput`), separate
 * from the computed public `SeasonArchive`, so a single-week edit can be
 * applied without losing the rest of the season's results.
 */
export interface ManualTeamInput {
  name: string;
  managerNames?: string[];
  logoUrl?: string | null;
}

export interface ManualWeekResult {
  week: number;
  /** `team` must exactly match a `name` in this season's `teams` list. */
  winner: { team: string; points: number };
  loser: { team: string; points: number };
}

/**
 * Every season has exactly one owner. A season Yahoo has published belongs
 * to the weekly fetcher, which rebuilds it from Yahoo and supersedes any
 * hand-entered data for it. A season only ever entered by hand belongs to
 * the admin editor (`packages/api`'s `seasonEditor.ts`), and the fetcher
 * leaves it alone. The published archive records which: a hand-entered
 * season's `leagueKey` is always `manualLeagueKey(season)`, while Yahoo's
 * are its own (e.g. "449.l.222").
 */
export function manualLeagueKey(season: number): string {
  return `manual.${season}`;
}

/** True when a published season was entered by hand rather than fetched from Yahoo. */
export function isManualSeason(archive: Pick<SeasonArchive, "season" | "leagueKey">): boolean {
  return archive.leagueKey === manualLeagueKey(archive.season);
}

export interface ManualSeasonInput {
  season: number;
  leagueName: string;
  /** Set true once every week you intend to enter (through week 14) is in. */
  complete: boolean;
  /** Roster; entry order sets each team's stable synthetic id. */
  teams: ManualTeamInput[];
  /** One entry per week that's been played. Omit weeks not played yet. */
  results: ManualWeekResult[];
}
